import { pickText } from '@telestore/contracts';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { useShop } from '../lib/shop-context.js';
import { ShopFooter, ShopHeader } from '../components/chrome.js';
import { ProductList } from '../components/product-list.js';
import { EmptyState, ErrorNotice, SkeletonCard } from '../components/primitives.js';
import { SuspendedNotice } from '../components/trust.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/field.js';
import { ratioToCss } from '@telestore/theming';

/**
 * Shop home. The layout — grid density, card ratio, category nav style, hero
 * banner — is entirely driven by the resolved template (§5), so this component
 * reads the config and never decides for itself.
 */
export function ShopPage() {
  const { data, locale, t } = useShop();
  const shop = data.shop;
  const layout = data.resolvedTheme.layout;
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const categories = useQuery({
    queryKey: ['categories', shop.slug],
    queryFn: () => api.categories(shop.slug),
    staleTime: 5 * 60_000,
  });

  const products = useInfiniteQuery({
    queryKey: ['products', shop.slug, categoryId, search],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.products(shop.slug, {
        ...(categoryId ? { categoryId } : {}),
        ...(search ? { q: search } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = products.data?.pages.flatMap((page) => page.items) ?? [];
  const cover = data.resolvedTheme.cover ?? shop.theme.cover;
  // Narrowed once so the nav components take a two-value union rather than
  // re-checking 'none' at every call site.
  const navVariant = layout.categories_nav === 'none' ? null : layout.categories_nav;

  /**
   * §6.7: a suspended shop shows a notice instead of a catalogue.
   *
   * The header and footer stay, so the customer can still reach the merchant
   * and still report the shop — the two things left worth doing here.
   */
  if (shop.status !== 'active') {
    return (
      <div className="min-h-dvh">
        <ShopHeader />
        <SuspendedNotice />
        <ShopFooter />
      </div>
    );
  }

  return (
    <div className="min-h-dvh">
      <ShopHeader />

      {layout.home_banner && cover ? (
        <div className="px-4 pt-4">
          <img
            src={cover}
            alt=""
            className="h-40 w-full rounded-[var(--radius-card)] object-cover"
            loading="eager"
          />
        </div>
      ) : null}

      <div className="px-4 pt-4">
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('storefront.searchProducts')}
          aria-label={t('storefront.searchProducts')}
        />
      </div>

      {navVariant && (categories.data?.categories.length ?? 0) > 0 ? (
        <nav
          className={
            navVariant === 'tabs'
              ? 'mt-4 flex gap-4 overflow-x-auto px-4 text-sm'
              : 'mt-4 flex gap-2 overflow-x-auto px-4'
          }
          style={
            navVariant === 'tabs'
              ? { borderBottom: '1px solid var(--color-border)' }
              : undefined
          }
          aria-label={t('storefront.categories')}
        >
          <CategoryButton
            variant={navVariant}
            active={categoryId === null}
            onClick={() => setCategoryId(null)}
            label={t('common.all')}
          />
          {categories.data?.categories.map((category) => (
            <CategoryButton
              key={category.id}
              variant={navVariant}
              active={categoryId === category.id}
              onClick={() => setCategoryId(category.id)}
              label={pickText(category.name, locale)}
            />
          ))}
        </nav>
      ) : null}

      <main className="pt-6">
        {products.isPending ? (
          <div className="grid grid-cols-2 gap-x-3 gap-y-6 px-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <SkeletonCard key={index} ratio={ratioToCss(layout.card_image_ratio)} />
            ))}
          </div>
        ) : products.isError ? (
          <ErrorNotice
            message={(products.error as Error).message}
            onRetry={() => void products.refetch()}
            retryLabel={t('common.retry')}
          />
        ) : items.length === 0 ? (
          <EmptyState title={t('storefront.noProducts')} />
        ) : (
          <>
            <ProductList
              products={items}
              shopSlug={shop.slug}
              currency={shop.currency}
              locale={locale}
              layout={layout}
              outOfStockLabel={t('common.outOfStock')}
              lowStockLabel={(count) => t('common.lowStock', { count })}
            />
            {products.hasNextPage ? (
              <div className="px-4 pt-8">
                <Button variant="ghost"
                  disabled={products.isFetchingNextPage}
                  onClick={() => void products.fetchNextPage()}
                >
                  {products.isFetchingNextPage ? t('common.loading') : t('common.next')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </main>

      <ShopFooter />
    </div>
  );
}

function CategoryButton({
  variant,
  active,
  onClick,
  label,
}: {
  variant: 'chips' | 'tabs';
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  if (variant === 'tabs') {
    return (
      <button
        type="button"
        onClick={onClick}
        className="shrink-0 whitespace-nowrap pb-2 font-medium"
        style={{
          borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
          opacity: active ? 1 : 0.6,
        }}
      >
        {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium"
      style={
        active
          ? { background: 'var(--color-primary)', color: 'var(--color-primary-contrast)' }
          : { background: 'var(--color-surface)', color: 'var(--color-text)' }
      }
    >
      {label}
    </button>
  );
}
